import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Secciones/Header';
import Footer from '../components/Secciones/Footer';
import Automation1 from '../assets/images/Automation1.jpeg';
import Automation2 from '../assets/images/Automation2.webp';
import Automation3 from '../assets/images/Automation3.webp';
import Automation4 from '../assets/images/Automation4.jpeg';
import Automation5 from '../assets/images/Automation5.webp';
import PlcImage from '../assets/images/PLC.jpg';
import './AutomationInfo.css';

const topics = [
  {
    id: 'automation',
    number: '01',
    eyebrow: 'Factory foundations',
    title: 'Industrial automation equipment',
    intro: 'Reliable automation begins with components that sense, decide, communicate and act as one coordinated system.',
    image: Automation1,
    alt: 'Industrial automation equipment in a production environment',
    features: [
      { icon: 'fa-microchip', title: 'Control', text: 'PLCs, I/O and industrial computers coordinate equipment and process logic.' },
      { icon: 'fa-eye', title: 'Sensing', text: 'Sensors and machine vision turn physical conditions into useful signals.' },
      { icon: 'fa-shield-alt', title: 'Safety', text: 'Safety relays, scanners and interlocks protect people and production.' }
    ],
    products: ['PLCs', 'Industrial sensors', 'Safety controls', 'HMIs']
  },
  {
    id: 'machinelearning',
    number: '02',
    eyebrow: 'From data to action',
    title: 'Machine learning & smart systems',
    intro: 'Connected data and intelligent analysis help teams detect patterns, anticipate issues and improve quality decisions.',
    image: Automation2,
    alt: 'Connected smart manufacturing system',
    features: [
      { icon: 'fa-chart-line', title: 'Predictive insight', text: 'Condition data can reveal maintenance needs before a failure stops production.' },
      { icon: 'fa-camera', title: 'Vision inspection', text: 'Cameras and vision processors support repeatable inspection and traceability.' },
      { icon: 'fa-database', title: 'Useful data', text: 'Edge and network devices make operating information available where it matters.' }
    ],
    products: ['Machine vision', 'Smart sensors', 'Industrial networking', 'Edge devices']
  },
  {
    id: 'robotics',
    number: '03',
    eyebrow: 'Precision in motion',
    title: 'Robotics & motion control',
    intro: 'Robotics combines controlled movement, feedback and safety to make repetitive or demanding operations more consistent.',
    image: Automation3,
    alt: 'Industrial robotic automation',
    features: [
      { icon: 'fa-robot', title: 'Robotic cells', text: 'Flexible systems for assembly, handling, packaging, welding and inspection.' },
      { icon: 'fa-sync-alt', title: 'Motion control', text: 'Servo drives, motors and feedback devices provide accurate positioning.' },
      { icon: 'fa-hands-helping', title: 'Safe interaction', text: 'Safety devices and engineered guarding support productive human-machine work.' }
    ],
    products: ['Servo drives', 'Encoders', 'Safety scanners', 'Pneumatic actuators']
  },
  {
    id: 'manufacturing',
    number: '04',
    eyebrow: 'Repeatable production',
    title: 'Consumer product manufacturing',
    intro: 'Well-designed automation helps manufacturers maintain throughput, product quality and traceability as demand changes.',
    image: Automation4,
    alt: 'Automated consumer product manufacturing line',
    features: [
      { icon: 'fa-box', title: 'Packaging', text: 'Detection, motion and control components support high-speed packaging processes.' },
      { icon: 'fa-search', title: 'Quality assurance', text: 'Vision and sensing technologies verify presence, position and product condition.' },
      { icon: 'fa-route', title: 'Traceability', text: 'Identification and communication products connect material flow with production records.' }
    ],
    products: ['Barcode readers', 'Photoelectric sensors', 'Conveying controls', 'Vision systems']
  },
  {
    id: 'industry4',
    number: '05',
    eyebrow: 'Connected operations',
    title: 'Industry 4.0 infrastructure',
    intro: 'Digital transformation is built on practical connections between equipment, information and the people responsible for performance.',
    image: Automation5,
    alt: 'Connected Industry 4.0 manufacturing',
    features: [
      { icon: 'fa-network-wired', title: 'Connectivity', text: 'Industrial networks link controllers, devices and supervisory platforms.' },
      { icon: 'fa-desktop', title: 'Visibility', text: 'HMIs and monitoring tools make machine state and operating trends easier to understand.' },
      { icon: 'fa-bolt', title: 'Efficiency', text: 'Power monitoring and connected controls help teams identify avoidable operating losses.' }
    ],
    products: ['Industrial Ethernet', 'Remote I/O', 'Power monitoring', 'Panel connectivity']
  },
  {
    id: 'plc',
    number: '06',
    eyebrow: 'The control layer',
    title: 'Programmable logic controllers',
    intro: 'PLCs provide dependable, maintainable control for machines and processes ranging from compact equipment to distributed production lines.',
    image: PlcImage,
    alt: 'Programmable logic controller hardware',
    features: [
      { icon: 'fa-puzzle-piece', title: 'Scalable architecture', text: 'Compact and modular platforms adapt to different I/O and communication needs.' },
      { icon: 'fa-exchange-alt', title: 'System integration', text: 'Communication modules connect field devices, drives, HMIs and higher-level systems.' },
      { icon: 'fa-tools', title: 'Serviceability', text: 'Structured diagnostics and replaceable modules help maintenance teams work efficiently.' }
    ],
    products: ['PLC CPUs', 'I/O modules', 'Power supplies', 'Communication modules']
  }
];

const navTopics = [
  ['automation', 'Automation'],
  ['machinelearning', 'Smart systems'],
  ['robotics', 'Robotics'],
  ['manufacturing', 'Manufacturing'],
  ['industry4', 'Industry 4.0'],
  ['plc', 'PLCs']
];

export default function AutomationInfo() {
  return (
    <div className="ai-page">
      <Header />
      <main>
        <section className="ai-hero">
          <div className="ai-container ai-hero-grid">
            <div>
              <span className="ai-eyebrow">Automation knowledge center</span>
              <h1>Understand the system.<br /><em>Source it smarter.</em></h1>
              <p>
                A practical guide to the technologies that connect, control and
                improve modern manufacturing operations.
              </p>
              <div className="ai-hero-actions">
                <a href="#automation">Explore the guide <span>↓</span></a>
                <Link to="/search?q=*">Browse products <span>↗</span></Link>
              </div>
            </div>
            <div className="ai-hero-visual">
              <img src={Automation1} alt="Modern automated manufacturing equipment" />
              <div>
                <span>Six connected layers</span>
                <p>From field sensing and machine control to information, motion and safety.</p>
              </div>
            </div>
          </div>
        </section>

        <nav className="ai-topic-nav" aria-label="Automation topics">
          <div className="ai-container">
            <span>Jump to</span>
            {navTopics.map(([id, label]) => <a href={`#${id}`} key={id}>{label}</a>)}
          </div>
        </nav>

        <section className="ai-intro">
          <div className="ai-container ai-intro-grid">
            <div>
              <span className="ai-eyebrow ai-eyebrow-dark">A connected view</span>
              <h2>Automation is not one product. It is a system of decisions.</h2>
            </div>
            <div>
              <p>
                Every successful automation project connects a real operating need
                with the right combination of sensing, control, communication, motion
                and safety. Understanding those relationships makes component selection
                more effective and long-term support more manageable.
              </p>
              <div className="ai-intro-note">
                <i className="fas fa-lightbulb" />
                <p><strong>Start with the application.</strong><span>Operating environment, inputs, outputs, cycle time and safety requirements should guide product selection.</span></p>
              </div>
            </div>
          </div>
        </section>

        <div className="ai-topics">
          {topics.map((topic, index) => (
            <section id={topic.id} className={`ai-topic ${index % 2 ? 'is-reversed' : ''}`} key={topic.id}>
              <div className="ai-container ai-topic-grid">
                <div className="ai-topic-media">
                  <img src={topic.image} alt={topic.alt} loading="lazy" />
                  <span>{topic.number}</span>
                </div>
                <div className="ai-topic-copy">
                  <span className="ai-eyebrow ai-eyebrow-dark">{topic.eyebrow}</span>
                  <h2>{topic.title}</h2>
                  <p className="ai-topic-intro">{topic.intro}</p>
                  <div className="ai-feature-list">
                    {topic.features.map((feature) => (
                      <article key={feature.title}>
                        <i className={`fas ${feature.icon}`} />
                        <div><h3>{feature.title}</h3><p>{feature.text}</p></div>
                      </article>
                    ))}
                  </div>
                  <div className="ai-product-links">
                    <span>Related product families</span>
                    <div>
                      {topic.products.map((product) => (
                        <Link to={`/search?q=${encodeURIComponent(product)}&page=1`} key={product}>
                          {product} <b>↗</b>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          ))}
        </div>

        <section className="ai-selection">
          <div className="ai-container">
            <div className="ai-selection-heading">
              <span className="ai-eyebrow">Before selecting a component</span>
              <h2>Define the requirement<br />that the product must solve.</h2>
            </div>
            <div className="ai-selection-grid">
              {[
                ['01', 'Application', 'What action, measurement or control decision is required?'],
                ['02', 'Environment', 'Consider voltage, enclosure, temperature, washdown and hazardous conditions.'],
                ['03', 'Integration', 'Identify I/O, protocols, mechanical fit and compatibility with existing equipment.'],
                ['04', 'Lifecycle', 'Plan for availability, documentation, serviceability and future expansion.']
              ].map(([number, title, text]) => (
                <article key={number}><span>{number}</span><h3>{title}</h3><p>{text}</p></article>
              ))}
            </div>
          </div>
        </section>

        <section className="ai-cta">
          <div className="ai-container">
            <div>
              <span className="ai-eyebrow">Turn the guide into a shortlist</span>
              <h2>Bring us the application.</h2>
            </div>
            <div>
              <p>Share a part number, control challenge or project list and our team will help you identify practical product options.</p>
              <Link to="/about#contact">Discuss your requirement <span>→</span></Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
